import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { useTranslation } from "../LanguageContext";
import { UserContext } from '../UserContext';
import { countries as countryList } from "../utils/countries";
import { PLATFORMS, SOCIAL_PLATFORM_KEYS, PlatformIcon } from "../utils/platformIcons";
import { ReputationBadge } from "../components/ReputationBadge";
import { AvailabilityChip } from "../components/AvailabilityPanel";

/**
 * Search — the one screen where a brand decides whose profile to open.
 *
 * It had fallen a long way behind the rest of the product. A result was a name,
 * a follower count and a row of chips; everything the platform learned about an
 * account since — how it has been rated, whether it is even taking work, what it
 * charges, whether it has a press page — lived on the profile you had to open
 * first. The card now carries all of it, straight off `_search_card`, which is
 * the same row the applicant list and brand discovery already show.
 *
 * Three things changed shape here:
 *
 * * **One search, not two tabs.** The username box and the filter sheet were
 *   separate modes hitting separate endpoints that answered in *different*
 *   shapes. There is one query now: a name is just another filter, and the
 *   page opens on a ranked list rather than on nothing.
 * * **Platforms come from `utils/platformIcons`**, so the picker is the 26
 *   accounts a profile can actually connect rather than the five that were
 *   typed into this file by hand.
 * * **The country filter is labelled for what it filters.** It reads the
 *   account's own listed countries; calling that "target audience location"
 *   promised an audience breakdown that does not exist on this endpoint.
 */

const PAGE = 12;
const MAX_COUNTRIES = 5;
const MAX_TAGS = 10;
const AVAILABILITY_OPTIONS = ['open', 'limited', 'busy', 'closed'];

const compact = (value) => {
    const n = Number(value || 0);
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return String(n);
};

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

const labelOf = (item) => (typeof item === 'string' ? item : (item?.name ?? ''));

const CheckIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" {...props}>
        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
);

/** A filter that is either on or off. Reads as a chip so a row of them fits a
 *  phone without becoming a column of checkboxes. */
function Chip({ active, onClick, children, tone = 'primary' }) {
    const on = {
        primary: 'bg-primary-600 text-white border-primary-600',
        emerald: 'bg-emerald-600 text-white border-emerald-600',
        amber: 'bg-amber-500 text-white border-amber-500',
    }[tone];

    return (
        <button type="button" onClick={onClick}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold border flex-shrink-0 whitespace-nowrap transition
                ${active ? on : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
            {children}
        </button>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{label}</label>
            {children}
        </div>
    );
}

/**
 * One result.
 *
 * The whole card used to be a single `<Link>`, which is why it could never
 * offer a second destination — a press page link nested inside it would be an
 * anchor inside an anchor. The name and the avatar link; the actions sit at the
 * bottom, the same way `CreatorDiscovery` lays out the identical payload.
 */
function ResultCard({ item, t }) {
    const rate = item.rate_card;
    const platforms = item.platforms || [];
    const tags = (item.tags || []).map(labelOf).filter(Boolean);
    const countries = (item.countrys || []).map(labelOf).filter(Boolean);
    // A platform search means the follower number the filter matched, not a
    // total the reader never asked about.
    const matched = item.matched_platform;

    return (
        <div className="card hover:shadow-md hover:border-primary-200 transition duration-200">
            <div className="flex items-start gap-3">
                <Link to={`/${item.username}`} className="relative flex-shrink-0">
                    <img src={item.profile_picture} alt=""
                        className="w-12 h-12 rounded-full object-cover ring-2 ring-slate-100" />
                    {(item.is_brand || item.is_content_producer) && (
                        <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white
                            ${item.verified ? (item.is_brand ? 'bg-yellow-500' : 'bg-blue-500') : 'bg-slate-400'}`}
                            title={item.verified
                                ? (item.is_brand ? t('profile.verifiedBrand') : t('profile.verifiedCreator'))
                                : (item.is_brand ? t('profile.unverifiedBrand') : t('profile.unverifiedCreator'))}>
                            <CheckIcon className="w-2.5 h-2.5 text-white" />
                        </span>
                    )}
                </Link>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Link to={`/${item.username}`}
                            className="font-bold text-slate-800 text-sm truncate no-underline hover:underline">
                            {item.username}
                        </Link>
                        <ReputationBadge rating={item.rating} compact />
                        <AvailabilityChip availability={item.availability} compact />
                    </div>

                    {item.profile_text && (
                        <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mt-0.5">
                            {item.profile_text}
                        </p>
                    )}

                    {/* The numbers, which is what the eye is here for. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-slate-500">
                        {matched ? (
                            <span>
                                <strong className="text-slate-700 tabular-nums">{compact(matched.followers)}</strong>{' '}
                                {t('campaigns.followers')} · {matched.social_media}
                            </span>
                        ) : item.followers > 0 && (
                            <span>
                                <strong className="text-slate-700 tabular-nums">{compact(item.followers)}</strong>{' '}
                                {t('campaigns.followers')}
                            </span>
                        )}
                        {item.avg_reach > 0 && (
                            <span><strong className="text-slate-700 tabular-nums">{compact(item.avg_reach)}</strong> {t('discover.reach')}</span>
                        )}
                        {item.engagement_rate > 0 && (
                            <span><strong className="text-slate-700 tabular-nums">%{item.engagement_rate}</strong> {t('discover.engagement')}</span>
                        )}
                        {rate?.starting_usd != null && (
                            <span className="text-primary-700 font-bold tabular-nums">
                                {t('discover.from')} {usd(rate.starting_usd)}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {platforms.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                    {platforms.map((platform) => (
                        <a key={platform.id} href={platform.link} target="_blank" rel="noopener noreferrer"
                            title={`${platform.social_media} · ${compact(platform.followers)}`}
                            className="flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 transition no-underline">
                            <PlatformIcon platform={platform.social_media} size={18} />
                            <span className="text-[10px] font-semibold text-slate-600 tabular-nums">
                                {compact(platform.followers)}
                            </span>
                        </a>
                    ))}
                </div>
            )}

            {(tags.length > 0 || countries.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {countries.slice(0, 3).map((country, i) => (
                        <span key={`c${i}`} className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200/60">
                            🌍 {country}
                        </span>
                    ))}
                    {countries.length > 3 && (
                        <span className="text-[10px] text-slate-400 px-1 py-0.5">
                            {t('search.moreTags').replace('{n}', countries.length - 3)}
                        </span>
                    )}
                    {tags.slice(0, 4).map((tag, i) => (
                        <span key={`t${i}`} className="text-[10px] text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full border border-primary-100/50">
                            🏷️ {tag}
                        </span>
                    ))}
                    {tags.length > 4 && (
                        <span className="text-[10px] text-slate-400 px-1 py-0.5">
                            {t('search.moreTags').replace('{n}', tags.length - 4)}
                        </span>
                    )}
                </div>
            )}

            <div className="flex gap-2 mt-3">
                <Link to={`/${item.username}`}
                    className="btn-secondary flex-1 py-2 text-xs text-center no-underline">
                    {t('search.viewProfile')}
                </Link>
                {/* Only a published press page is linked — the kit is also open
                    to a brand this creator applied to, but search cannot know
                    that, and a link that 403s is worse than no link. */}
                {item.media_kit && (
                    <Link to={`/${item.username}/media-kit`}
                        className="btn-primary flex-1 py-2 text-xs text-center no-underline">
                        {t('search.mediaKit')}
                    </Link>
                )}
            </div>
        </div>
    );
}

function Search() {
    const { t } = useTranslation();
    const { user } = useContext(UserContext);

    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(PAGE);
    const [isover, setIsOver] = useState(true);
    const [loading, setLoading] = useState(true);
    /* An error *code*, translated at render. Storing the translated string
       would freeze it in whichever language was live when it happened. */
    const [errorKey, setErrorKey] = useState(null);

    const [showFilters, setShowFilters] = useState(false);
    const [showWarning, setShowWarning] = useState(false);

    const [name, setName] = useState("");
    const [role, setRole] = useState("");              // '' | 'creator' | 'brand'
    const [platform, setPlatform] = useState("");
    const [sort, setSort] = useState("");
    const [minFollowers, setMinFollowers] = useState("");
    const [maxFollowers, setMaxFollowers] = useState("");
    const [minEngagement, setMinEngagement] = useState("");
    const [budget, setBudget] = useState("");
    const [availability, setAvailability] = useState("");
    const [openToWork, setOpenToWork] = useState(false);
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [hasMediaKit, setHasMediaKit] = useState(false);
    const [hasRateCard, setHasRateCard] = useState(false);
    const [countrys, setCountrys] = useState([]);
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [tagSuggestions, setTagSuggestions] = useState([]);

    /* `user.tags` is the whole tag vocabulary, not this account's own tags. */
    useEffect(() => {
        if (tagInput === "") {
            setTagSuggestions([]);
            return;
        }
        const draft = tagInput.toLowerCase();
        setTagSuggestions((user?.tags || [])
            .filter((tag) => tag.name.toLowerCase().startsWith(draft))
            .slice(0, 8));
    }, [tagInput, user]);

    const criteria = useMemo(() => ({
        name: name.trim(),
        socialMedia: platform,
        sort,
        minFollowers,
        maxFollowers,
        minEngagement,
        budget,
        availability,
        countrys,
        tags,
        is_content_producer: role === 'creator',
        is_brand: role === 'brand',
        open_to_work: openToWork,
        verified_only: verifiedOnly,
        has_media_kit: hasMediaKit,
        has_rate_card: hasRateCard,
    }), [name, platform, sort, minFollowers, maxFollowers, minEngagement, budget,
        availability, countrys, tags, role, openToWork, verifiedOnly, hasMediaKit, hasRateCard]);

    /* Any change of criteria is a new search, so paging starts over — asking
       for page 4 of a query nobody ran yet returns a middle nobody saw. */
    useEffect(() => { setLimit(PAGE); }, [criteria]);

    const activeCount = useMemo(() => [
        platform, sort, minFollowers, maxFollowers, minEngagement, budget, availability, role,
        openToWork, verifiedOnly, hasMediaKit, hasRateCard,
        countrys.length, tags.length,
    ].filter(Boolean).length, [platform, sort, minFollowers, maxFollowers, minEngagement, budget,
        availability, role, openToWork, verifiedOnly, hasMediaKit, hasRateCard, countrys, tags]);

    useEffect(() => {
        if (minFollowers !== "" && maxFollowers !== "" && Number(minFollowers) > Number(maxFollowers)) {
            setErrorKey('search.minMaxAlert');
            return undefined;
        }
        if (Number(minFollowers) < 0 || Number(maxFollowers) < 0) {
            setErrorKey('search.negativeFollowersAlert');
            return undefined;
        }
        setErrorKey(null);

        let cancelled = false;
        setLoading(true);
        // Debounced because the search box is a filter now: every keystroke
        // would otherwise be a scan over the whole user table.
        const timer = setTimeout(async () => {
            try {
                const res = await api.post('api/filter/', { ...criteria, range: limit });
                if (cancelled) return;
                const rows = res.data.data || [];
                setResults(rows);
                setTotal(res.data.count ?? rows.length);
                setIsOver(res.data.isover);
            } catch (error) {
                if (!cancelled) setErrorKey('search.errorMsg');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [criteria, limit, minFollowers, maxFollowers]);

    const clearFilters = () => {
        setRole("");
        setPlatform("");
        setSort("");
        setMinFollowers("");
        setMaxFollowers("");
        setMinEngagement("");
        setBudget("");
        setAvailability("");
        setOpenToWork(false);
        setVerifiedOnly(false);
        setHasMediaKit(false);
        setHasRateCard(false);
        setCountrys([]);
        setTags([]);
        setTagInput("");
    };

    const addCountry = (event) => {
        const selected = event.target.value;
        event.target.value = "";
        if (!selected) return;
        if (countrys.length >= MAX_COUNTRIES) {
            setErrorKey('search.maxCountryAlert');
            return;
        }
        if (!countrys.includes(selected)) setCountrys([...countrys, selected]);
    };

    const addTag = (tagName) => {
        if (!tagName) return;
        if (tags.length >= MAX_TAGS) {
            setErrorKey('search.maxTagAlert');
            return;
        }
        if (!tags.includes(tagName)) setTags([...tags, tagName]);
        setTagInput("");
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-24">
            {showWarning && (
                <div className="modal-overlay" onClick={() => setShowWarning(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-red-100 bg-red-50 flex items-center gap-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h2 className="text-lg font-semibold text-red-800">{t('search.safetyTitle')}</h2>
                        </div>
                        <div className="p-6 text-sm text-slate-600 leading-relaxed">{t('search.safetyBody')}</div>
                        <div className="px-6 py-3 bg-slate-50 flex justify-end">
                            <button type="button" className="btn-secondary text-sm" onClick={() => setShowWarning(false)}>
                                {t('search.safetyClose')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3">
                <div className="max-w-xl mx-auto flex flex-col gap-2.5">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input type="text" className="input w-full pl-9" value={name} maxLength={254}
                                placeholder={t('search.placeholder')}
                                onChange={(event) => setName(event.target.value)} />
                        </div>

                        <button type="button" onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? t('search.hideFilters') : t('search.filterBtn')}
                            className={`relative w-11 h-11 flex items-center justify-center rounded-xl border transition flex-shrink-0
                                ${showFilters || activeCount > 0
                                    ? 'bg-primary-50 text-primary-600 border-primary-200'
                                    : 'bg-slate-100 text-slate-500 border-transparent hover:bg-slate-200'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            {activeCount > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-primary-600 text-white text-[10px] font-bold flex items-center justify-center">
                                    {activeCount}
                                </span>
                            )}
                        </button>

                        <button type="button" onClick={() => setShowWarning(true)}
                            title={t('search.safetyTitle')}
                            className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </button>
                    </div>

                    {/* The filters people reach for most, one tap away. */}
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-0.5">
                        <Chip active={role === 'creator'} onClick={() => setRole(role === 'creator' ? "" : 'creator')}>
                            {t('search.creators')}
                        </Chip>
                        <Chip active={role === 'brand'} onClick={() => setRole(role === 'brand' ? "" : 'brand')} tone="amber">
                            {t('search.brands')}
                        </Chip>
                        <Chip active={openToWork} onClick={() => setOpenToWork(!openToWork)} tone="emerald">
                            {t('availability.openToWork')}
                        </Chip>
                        <Chip active={verifiedOnly} onClick={() => setVerifiedOnly(!verifiedOnly)}>
                            {t('search.verifiedOnly')}
                        </Chip>
                        <Chip active={hasMediaKit} onClick={() => setHasMediaKit(!hasMediaKit)}>
                            {t('search.hasMediaKit')}
                        </Chip>
                        <Chip active={hasRateCard} onClick={() => setHasRateCard(!hasRateCard)}>
                            {t('search.hasRateCard')}
                        </Chip>
                    </div>

                    {showFilters && (
                        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-4 animate-fade-in-up">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label={t('search.platform')}>
                                    {/* Every platform a profile can connect, from the same
                                        table the icons come from — this list was five
                                        options typed in by hand. */}
                                    <select className="input py-2 text-sm" value={platform}
                                        onChange={(e) => setPlatform(e.target.value)}>
                                        <option value="">{t('search.allPlatforms')}</option>
                                        {SOCIAL_PLATFORM_KEYS.map((key) => (
                                            <option key={key} value={PLATFORMS[key].label}>{PLATFORMS[key].label}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label={t('search.sortBy')}>
                                    <select className="input py-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
                                        <option value="">{t('search.sortRecommended')}</option>
                                        <option value="-">{t('search.sortMostFollowers')}</option>
                                        <option value="+">{t('search.sortLeastFollowers')}</option>
                                        <option value="rating">{t('search.sortRating')}</option>
                                        <option value="engagement">{t('search.sortEngagement')}</option>
                                        <option value="price">{t('search.sortPrice')}</option>
                                    </select>
                                </Field>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label={t('search.minFollowers')}>
                                    <input type="number" min="0" className="input py-2 text-sm" placeholder="0"
                                        value={minFollowers} onChange={(e) => setMinFollowers(e.target.value)} />
                                </Field>
                                <Field label={t('search.maxFollowers')}>
                                    <input type="number" min="0" className="input py-2 text-sm" placeholder="1000000"
                                        value={maxFollowers} onChange={(e) => setMaxFollowers(e.target.value)} />
                                </Field>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label={t('search.minEngagement')}>
                                    <input type="number" min="0" step="0.1" className="input py-2 text-sm" placeholder="3.5"
                                        value={minEngagement} onChange={(e) => setMinEngagement(e.target.value)} />
                                </Field>
                                <Field label={t('search.budget')}>
                                    <input type="number" min="0" className="input py-2 text-sm" placeholder="500"
                                        value={budget} onChange={(e) => setBudget(e.target.value)} />
                                </Field>
                            </div>
                            {budget !== "" && (
                                <p className="text-[11px] text-slate-400 leading-relaxed -mt-2">{t('search.budgetHint')}</p>
                            )}

                            <Field label={t('search.availabilityLabel')}>
                                <select className="input py-2 text-sm" value={availability}
                                    onChange={(e) => setAvailability(e.target.value)}>
                                    <option value="">{t('search.anyAvailability')}</option>
                                    {AVAILABILITY_OPTIONS.map((value) => (
                                        <option key={value} value={value}>{t(`availability.status_${value}`)}</option>
                                    ))}
                                </select>
                            </Field>

                            <hr className="border-slate-100" />

                            <Field label={t('search.countryPlaceholder')}>
                                <select className="input py-2 text-sm mb-1" onChange={addCountry} defaultValue="">
                                    <option value="" disabled>{t('search.countryPlaceholder')}</option>
                                    {countryList.map((country, idx) => (
                                        <option key={idx} value={country}>{country}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 mb-2">{t('search.countryHint')}</p>
                                {countrys.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {countrys.map((country, idx) => (
                                            <span key={idx} onClick={() => setCountrys(countrys.filter((_, i) => i !== idx))}
                                                className="badge bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-500 cursor-pointer flex items-center gap-1 transition">
                                                🌍 {country} <span className="font-bold ml-1">×</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Field>

                            <Field label={t('search.tagsPlaceholder')}>
                                <div className="flex gap-2 mb-2 relative">
                                    <input type="text" className="input py-2 text-sm flex-1"
                                        placeholder={t('search.tagsPlaceholder')} value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }} />
                                    <button type="button" onClick={() => addTag(tagInput)} className="btn-secondary py-2 px-4 text-sm">
                                        {t('search.addBtn')}
                                    </button>

                                    {tagSuggestions.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-30 max-h-48 overflow-y-auto">
                                            {tagSuggestions.map((suggestion, idx) => (
                                                <button key={idx} type="button" onClick={() => addTag(suggestion.name)}
                                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition border-b border-slate-100 last:border-0">
                                                    {suggestion.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {tags.map((tag, idx) => (
                                            <span key={idx} onClick={() => setTags(tags.filter((_, i) => i !== idx))}
                                                className="badge bg-primary-50 text-primary-600 hover:bg-red-50 hover:text-red-500 cursor-pointer flex items-center gap-1 transition">
                                                🏷️ {tag} <span className="font-bold ml-1">×</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Field>

                            <div className="flex justify-end pt-1">
                                <button type="button" onClick={clearFilters}
                                    className="px-4 py-1.5 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition">
                                    {t('search.clearFilters')}
                                </button>
                            </div>
                        </div>
                    )}

                    {errorKey && (
                        <div className="bg-red-50 text-red-500 text-sm p-3 rounded-xl border border-red-100">
                            {t(errorKey)}
                        </div>
                    )}
                </div>
            </div>

            <div className="max-w-xl mx-auto px-4 pt-4 space-y-3">
                {!loading && results.length > 0 && (
                    <div className="flex items-center justify-between gap-3 px-1">
                        <p className="text-xs font-semibold text-slate-500 tabular-nums">
                            {t('search.results').replace('{n}', total)}
                        </p>
                        {/* Said wherever these numbers travel: nobody should read
                            a creator's own figures as platform-verified. */}
                        <p className="text-[10px] text-slate-400 text-right leading-tight">{t('search.selfReported')}</p>
                    </div>
                )}

                {loading && results.length === 0 ? (
                    <div className="flex justify-center py-16"><div className="spinner" /></div>
                ) : results.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <p className="text-sm font-bold text-slate-600">
                            {name || activeCount > 0 ? t('search.empty') : t('search.startTyping')}
                        </p>
                        <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">
                            {name || activeCount > 0 ? t('search.emptyDesc') : t('search.startTypingDesc')}
                        </p>
                    </div>
                ) : (
                    results.map((item) => <ResultCard key={item.id} item={item} t={t} />)
                )}

                {!isover && results.length > 0 && (
                    <div className="flex justify-center py-4">
                        <button type="button" onClick={() => setLimit(limit + PAGE)} disabled={loading}
                            className="text-primary-600 hover:text-primary-700 transition flex items-center gap-2 text-sm font-medium disabled:opacity-50">
                            {loading ? <span className="spinner-sm" /> : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                            )}
                            {t('search.loadMore')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Search;
